import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { ScanLine, CheckSquare, AlertTriangle, Baby, FileWarning } from 'lucide-react';
import { dashboardApi } from '@/lib/api';
import type { RadiologistDashboardData, Modality } from './types';
import {
  MetricCard, MetricsGrid, SectionCard, UrgencyBadge, GenericBadge,
  DashboardShell, ScrollTable, Th, Td, LoadingState, ErrorState,
} from './_shared';

const MODALITY_STYLE: Record<Modality, string> = {
  ULTRASOUND: 'bg-blue-50 text-blue-700 border-blue-200',
  XRAY:       'bg-gray-100 text-gray-600 border-gray-200',
  CT:         'bg-violet-50 text-violet-700 border-violet-200',
  MRI:        'bg-indigo-50 text-indigo-700 border-indigo-200',
};

function ModalityTag({ modality }: { modality: Modality }) {
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-semibold border', MODALITY_STYLE[modality])}>
      {modality}
    </span>
  );
}

// ── Component ───────────────────────────────────────────────────────────────
export default function RadiologistDashboard() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<RadiologistDashboardData>({
    queryKey:        ['dashboard-radiology'],
    queryFn:         () => dashboardApi.radiology().then(r => r.data),
    staleTime:       60_000,
    refetchInterval: 2 * 60_000,
  });

  if (isLoading) return <LoadingState/>;
  if (isError || !data) return <ErrorState onRetry={() => refetch()}/>;

  const pcpndtOrders = data.orders.filter(o => o.isObstetricUSG && !o.formFComplete);

  return (
    <DashboardShell
      title="Radiology Worklist"
      subtitle="Scan queue · PCPNDT compliance"
      onRefresh={() => refetch()}
      refreshing={isFetching}>

      <MetricsGrid>
        <MetricCard icon={ScanLine}     label="Pending Scans"    value={data.pendingScans}    accent="bg-orange/15"/>
        <MetricCard icon={CheckSquare}  label="Completed Today"  value={data.completedToday}  accent="bg-emerald-50"/>
        <MetricCard
          icon={Baby}
          label="PCPNDT Pending"
          value={data.pcpndtPending}
          accent="bg-red-50"
          alert={data.pcpndtPending > 0}
        />
        <MetricCard icon={FileWarning}  label="Form-F Missing"   value={pcpndtOrders.length}  accent="bg-amber-50" alert={pcpndtOrders.length > 0}/>
      </MetricsGrid>

      {/* PCPNDT Alert Banner */}
      {pcpndtOrders.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3.5 flex items-start gap-3">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0"/>
          <div>
            <p className="text-sm font-semibold text-amber-800">PCPNDT Compliance Alert</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {pcpndtOrders.length} obstetric USG order{pcpndtOrders.length > 1 ? 's' : ''} missing Form-F.
              Scanning is prohibited until Form-F is completed and signed.
            </p>
          </div>
        </div>
      )}

      {/* Worklist */}
      <SectionCard title={`Worklist (${data.orders.length})`}>
        <ScrollTable>
          <thead>
            <tr>
              <Th>Order ID</Th><Th>Patient</Th><Th>Age / Sex</Th>
              <Th>Modality</Th><Th>Exam</Th><Th>Priority</Th>
              <Th>Referred By</Th><Th>Ordered</Th><Th>Status</Th><Th>Form-F</Th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map(o => (
              <tr key={o.id} className={clsx(
                'hover:bg-gray-50 transition',
                o.isObstetricUSG && !o.formFComplete && 'bg-amber-50/40',
              )}>
                <Td><span className="font-mono text-xs text-navy">{o.id}</span></Td>
                <Td><span className="font-medium">{o.patientName}</span></Td>
                <Td muted>{o.age}y / {o.gender}</Td>
                <Td><ModalityTag modality={o.modality}/></Td>
                <Td>{o.examName}</Td>
                <Td><UrgencyBadge urgency={o.priority}/></Td>
                <Td muted>{o.referredBy}</Td>
                <Td muted>{o.orderedAt}</Td>
                <Td>
                  <GenericBadge
                    label={o.status === 'in_progress' ? 'Scanning' : o.status === 'reported' ? 'Reported' : 'Pending'}
                    variant={o.status === 'reported' ? 'success' : o.status === 'in_progress' ? 'info' : 'neutral'}
                  />
                </Td>
                <Td>
                  {o.isObstetricUSG ? (
                    o.formFComplete
                      ? <GenericBadge label="Done" variant="success"/>
                      : <GenericBadge label="Missing" variant="danger"/>
                  ) : (
                    <span className="text-gray-300 text-xs">N/A</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      </SectionCard>

    </DashboardShell>
  );
}
